import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import { CerebroTextButton } from '../../shared/components';
import { useApi } from '../../shared/context';
import { QueryKeys } from '../../shared/hooks';
import { DataRoomTeaser } from '../../shared/models';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { getResponseData } from '../../shared/utils/axios';

interface ResetTeaserDialogProps {
	dataRoomGuid: string | undefined;
	teaser: DataRoomTeaser | null;
	close: () => void;
}

export const ResetTeaserDialog: React.FC<ResetTeaserDialogProps> = ({ dataRoomGuid, teaser, close }) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();

	const onResetConfirmation = () => {
		if (teaser !== null) resetTeaser(teaser);
	};

	const { mutate: resetTeaser } = useMutation<DataRoomTeaser, unknown, DataRoomTeaser>(
		teaserToDelete =>
			getResponseData(
				api.post<DataRoomTeaser>(CerebroApiEndpoints.Teasers.Reset(dataRoomGuid, teaserToDelete.guid))
			),
		{
			onMutate: () => {
				close();
			},
			onSuccess: (_data, resetTeaser) => {
				client.invalidateQueries(QueryKeys.Teasers.Root(dataRoomGuid));
				client.invalidateQueries(QueryKeys.DataRoom.Confirmation(dataRoomGuid));
				snackbar.enqueueSnackbar('Teaser Reset.', { variant: 'success' });

				close();
			},
			onError: () => {
				client.invalidateQueries(QueryKeys.Teasers.Root(dataRoomGuid));
				client.invalidateQueries(QueryKeys.DataRoom.Confirmation(dataRoomGuid));
				snackbar.enqueueSnackbar('There was a problem reseting the teaser.', { variant: 'error' });
			},
		}
	);

	return (
		<Dialog open={teaser !== null} onClose={close}>
			<DialogTitle>Confirm Reset</DialogTitle>
			<DialogContent>
				<DialogContentText>
					Are you sure you want to reset this teaser for {teaser?.firstName} {teaser?.lastName} at{' '}
					{teaser?.bankName}?
				</DialogContentText>
			</DialogContent>
			<DialogActions>
				<CerebroTextButton onClick={close}>No</CerebroTextButton>
				<CerebroTextButton onClick={onResetConfirmation}>Yes</CerebroTextButton>
			</DialogActions>
		</Dialog>
	);
};
